import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useApiContext } from 'context/ApiContext';

import { ITIL_TRANSLATION_TYPE_BY_SCOPE } from '../constants';
import type { ItilConsolidatedField, ItilConsolidatedSection } from '../types';
import {
  buildConsolidatedRawDescription,
  parseConsolidatedTranslation
} from '../utils';
import {
  clearItilTranslationCache,
  getItilTranslationCache,
  setItilTranslationCache
} from '../translationCache';
import { useItilTranslation } from './useItilTranslation';

type UseItilConsolidatedTranslationOptions = {
  ticketId: number;
  fields: ItilConsolidatedField[];
  /** `''` cuando el ticket no tiene ITIL de cierre persistido (limpia caché local). */
  persistedItilText?: string | null;
  /** true cuando los datos del ticket/cierre ya terminaron de cargar */
  ticketLoaded?: boolean;
};

export function useItilConsolidatedTranslation({
  ticketId,
  fields,
  persistedItilText,
  ticketLoaded = false
}: UseItilConsolidatedTranslationOptions) {
  const { itilGenius } = useApiContext();
  const itil = useItilTranslation();
  const fieldLabels = useMemo(() => fields.map((f) => f.label), [fields]);
  const translationType = ITIL_TRANSLATION_TYPE_BY_SCOPE.closing;

  const fieldsRawKey = useMemo(
    () => buildConsolidatedRawDescription(fields),
    [fields]
  );
  const baselineRawRef = useRef<string | null>(null);

  const hydrateStoredTranslation = useCallback(
    (stored: string) => {
      const text = stored.trim();
      setItilTranslationCache(ticketId, 'closing', text);
      itil.hydrate(text);

      if (fieldsRawKey.trim()) {
        baselineRawRef.current = fieldsRawKey;
      }
    },
    [fieldsRawKey, itil, ticketId]
  );

  useEffect(() => {
    if (!ticketId || !ticketLoaded) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const stored = await itilGenius.GetTranslationByType(
        ticketId,
        translationType
      );

      if (cancelled) {
        return;
      }

      if (stored?.trim()) {
        hydrateStoredTranslation(stored);
        return;
      }

      if (persistedItilText === '') {
        clearItilTranslationCache(ticketId, 'closing');
        itil.reset();
        baselineRawRef.current = fieldsRawKey;
        return;
      }

      const cached = getItilTranslationCache(ticketId, 'closing');
      if (cached) {
        hydrateStoredTranslation(cached);
        return;
      }

      baselineRawRef.current = fieldsRawKey;
    };

    load().catch((): void => undefined);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, persistedItilText, ticketLoaded]);

  useEffect(() => {
    if (itil.status !== 'ready') {
      if (itil.status === 'idle' || itil.status === 'loading') {
        baselineRawRef.current = null;
      }
      return;
    }

    if (!fieldsRawKey.trim()) {
      return;
    }

    if (baselineRawRef.current === null) {
      baselineRawRef.current = fieldsRawKey;
      return;
    }

    if (fieldsRawKey !== baselineRawRef.current) {
      itil.markStale();
    }
  }, [fieldsRawKey, itil]);

  const handleTranslate = useCallback(
    async (overrideFields?: ItilConsolidatedField[]) => {
      const source = overrideFields ?? fields;
      const raw = buildConsolidatedRawDescription(source);
      if (!raw) {
        return null;
      }

      const text = await itil.translate(raw, { ticketId, translationType });
      if (text) {
        baselineRawRef.current = raw;
        setItilTranslationCache(ticketId, 'closing', text);
      }
      return text;
    },
    [fields, itil, ticketId, translationType]
  );

  const sections = useMemo((): ItilConsolidatedSection[] => {
    if (itil.translatedText?.trim()) {
      return parseConsolidatedTranslation(itil.translatedText, fieldLabels);
    }
    return fields
      .filter((f) => f.value?.trim())
      .map((f) => ({ label: f.label, text: '' }));
  }, [fieldLabels, fields, itil.translatedText]);

  return {
    ...itil,
    sections,
    handleTranslate
  };
}
